import { format, formatISO } from "date-fns";
import { useForm } from "react-hook-form";

import { News } from "@/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import TiptapWyswyg from "@/components/wyswyg";
import { queryClient } from "@/libs/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { useCreateNews } from "../api/create-news";
import { NewsSchemaFormData, newsSchema } from "../types/news-schema";

import { DeleteNewsDialog } from "./delete-news-dialog";

type NewsFormProps = {
  initialData?: News;
  congressId?: string;
  onClose: () => void;
};

function NewsForm(props: NewsFormProps) {
  const { t } = useTranslation();
  const { initialData, onClose, congressId } = props;

  const form = useForm<NewsSchemaFormData>({
    resolver: zodResolver(newsSchema),
    defaultValues: {
      title: initialData ? initialData.title : "",
      excerpt: initialData?.excerpt ? initialData.excerpt : "",
      data: initialData?.data ? initialData.data : "",
      notificationDate: initialData?.sendNotificationAfter
        ? format(
            initialData.sendNotificationAfter.replace("Z", ""),
            "yyyy-MM-dd'T'HH:mm",
          )
        : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      sendLater: initialData?.sendNotificationAfter ? true : false,
    },
    disabled: !!initialData,
  });

  // const { mutateAsync: updateNavigationItem } = useUpdateNavigationItem();
  const { mutateAsync: createNewsItem } = useCreateNews();

  //TODO: handler server error
  const onSubmit = async (values: NewsSchemaFormData) => {
    if (initialData) {
      // await updateNavigationItem({
      //   requestBody: values,
      //   menuItemId: initialData.id,
      // });
    } else {
      await createNewsItem({
        congressId,
        requestBody: {
          title: values.title,
          excerpt: values.excerpt,
          data: values.data,
          sendNotificationAfter:
            values.sendLater && values.notificationDate
              ? formatISO(values.notificationDate)
              : undefined,
        },
      });
    }

    /**
     * TODO: refetch properly. in *-navigatin-item-form.tsx call with congressId
     */
    await queryClient.refetchQueries({
      queryKey: ["news"],
    });
    onClose();
  };

  const sendLater = form.watch("sendLater");

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("news.title")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("news.title")}
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("news.titleHelp")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="excerpt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("news.excerpt")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("news.excerpt")}
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("news.excerptHelp")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="data"
            render={({ field }) => (
              <>
                <FormLabel className="block">{t("news.content")}</FormLabel>

                <TiptapWyswyg {...field} />
                <FormDescription>
                  {t("news.contentHelp")}
                </FormDescription>
              </>
            )}
          />
          <FormField
            control={form.control}
            name="sendLater"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={field.disabled}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>{t("news.schedule")}</FormLabel>
                </div>
              </FormItem>
            )}
          />

          {sendLater && (
            <FormField
              control={form.control}
              name="notificationDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("news.publishAt")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("news.schedule")}
                      type="datetime-local"
                      className="h-8 px-2 py-1"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="flex">
            {!initialData && (
              <>
                <Button size="sm" disabled={form.formState.isSubmitting}>
                  {t("news.save")}
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  type="button"
                  className="ml-auto"
                  onClick={onClose}
                >
                  {t("news.cancel")}
                </Button>
              </>
            )}
            {initialData && <DeleteNewsDialog newsId={initialData.id} />}
          </div>
        </div>
      </form>
    </Form>
  );
}

export { NewsForm };
